import { useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useCreateStatusMutation,
  useDeleteStatusMutation,
  useUpdateStatusMutation,
} from "../../../../features/bughurd/api";
import {
  setNewColumnTitle,
  setIsAddingColumn,
  setDeleteConfirmModal,
  updateColumn,
  setColumnOrder,
  setLoadingStatus,
  setPagination,
  setHasMore,
  setColumns,
  setScrollPosition,
  setGroupingData,
} from "@/features/bughurd/slice";
import {
  selectBoardState,
  selectColumns,
  selectColumnOrder,
  selectGroupingConfig,
  selectGroupingDataMemoized,
} from "@/features/bughurd/boardSelector";
import { useBoardData } from "./useBoardData";
import { useBoardFetch } from "./useBoardFetch";

export const useColumnOperations = (id?: string, isMyTaskView?: boolean) => {
  const { t } = useTranslation(["bugherd"]);
  const dispatch = useAppDispatch();

  const boardState = useAppSelector(selectBoardState);
  const columns = useAppSelector(selectColumns);
  const columnOrder = useAppSelector(selectColumnOrder);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const groupingData = useAppSelector(selectGroupingDataMemoized);

  const { newColumnTitle, columnToDelete, viewType } = boardState;
  const { groupBy } = groupingConfig;

  const { PAGE_LIMIT, previousTasksRef } = useBoardData();
  const { fetchBugsForGroup } = useBoardFetch();

  const [createStatus, { isLoading: isCreatingStatus }] =
    useCreateStatusMutation();
  const [updateStatus] = useUpdateStatusMutation();
  const [deleteStatus, { isLoading: isDeletingStatus }] =
    useDeleteStatusMutation();

  const addColumn = useCallback(async () => {
    if (!newColumnTitle.trim() || groupBy !== "Status") {
      if (groupBy !== "Status") {
        toast.error("Can only add columns when grouping by Status");
      }
      return;
    }

    if (isMyTaskView) {
      toast.error("Cannot add columns in My Tasks view");
      return;
    }

    const payload = {
      // status: newColumnTitle.toUpperCase(),
      status: newColumnTitle,
      project: id || "",
    };

    try {
      const res = await createStatus(payload).unwrap();
      if (res?.data?.decryptedData?._id) {
        dispatch(setNewColumnTitle(""));
        dispatch(setIsAddingColumn(false));
        toast.success("Column added successfully");

        if (viewType === "kanban") {
          const newColumnId = res.data.decryptedData?._id;
          const newColumnTitle = res.data.decryptedData?.status;

          dispatch(
            updateColumn({
              columnId: newColumnId,
              column: {
                id: newColumnId,
                title: newColumnTitle,
                color: res.data.decryptedData?.color,
                icon: res.data.decryptedData?.icon,
                tasks: [],
                totalCount: 0,
                description: "",
                hidden: false,
              },
            })
          );

          dispatch(setColumnOrder([...columnOrder, newColumnId]));
          dispatch(
            setGroupingData({
              ...groupingData,
              [newColumnId]: {
                id: newColumnId,
                title: newColumnTitle,
                color: res.data.decryptedData?.color,
                icon: res.data.decryptedData?.icon,
                type: "status",
                description: "",
                hidden: false,
              },
            })
          );
          dispatch(
            setLoadingStatus({
              key: newColumnId,
              loading: {
                isInitialLoading: true,
                isLoadingMore: false,
              },
            })
          );

          dispatch(
            setPagination({
              key: newColumnId,
              pagination: {
                page: 1,
                limit: PAGE_LIMIT,
              },
            })
          );

          dispatch(
            setHasMore({
              key: newColumnId,
              hasMore: true,
            })
          );

          previousTasksRef.current[newColumnId] = 0;

          const newGroupData = {
            id: newColumnId,
            title: newColumnTitle,
            color: res.data.decryptedData.color,
            icon: res.data.decryptedData.icon,
            type: "status",
          };

          fetchBugsForGroup(newColumnId, newGroupData, 1, PAGE_LIMIT, false);
        }
      } else {
        toast.error("Failed to add column");
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to add column");
    }
  }, [
    newColumnTitle,
    groupBy,
    isMyTaskView,
    id,
    createStatus,
    viewType,
    columnOrder,
    PAGE_LIMIT,
    previousTasksRef,
    fetchBugsForGroup,
    groupingData,
  ]);
  const updatedColumnDataRef = useRef<Record<string, any>>({});

  const handleColumnTitleChange = useCallback(
    async (columnId: string, newTitle: string) => {
      if (groupBy !== "Status") {
        toast.error("Can only rename columns when grouping by Status");
        return;
      }

      if (isMyTaskView) {
        toast.error("Cannot edit columns in My Tasks view");
        return;
      }

      const oldTitle = columns[columnId]?.title;
      const oldIcon = columns[columnId]?.icon;
      const oldGroupingData = { ...groupingData };
      dispatch(
        updateColumn({
          columnId,
          column: {
            title: newTitle,
          },
        })
      );
      const updatedGroupingData = {
        ...groupingData,
        [columnId]: {
          ...groupingData[columnId],
          title: newTitle,
        },
      };
      dispatch(setGroupingData(updatedGroupingData));

      try {
        const res = await updateStatus({
          statusId: columnId,
          status: newTitle,
          project: id || "",
        }).unwrap();

        if (res?.data?.decryptedData) {
          const correctColumnData = {
            title:
              res?.data?.decryptedData?.status || newTitle,
            icon: res?.data?.decryptedData?.icon,
            color: res?.data?.decryptedData?.color,
          };

          toast.success("Column title updated successfully");
          updatedColumnDataRef.current[columnId] = correctColumnData;
          dispatch(
            updateColumn({
              columnId,
              column: {
                title: newTitle,
                icon: res.data.decryptedData.icon,
              },
            })
          );
          dispatch(
            setGroupingData({
              ...groupingData,
              [columnId]: {
                ...groupingData[columnId],
                title:
                  res?.data?.decryptedData?.status ||
                  newTitle,
                icon: res?.data?.decryptedData?.icon,
                color: res?.data?.decryptedData?.color,
              },
            })
          );

          if (viewType === "kanban") {
            dispatch(
              setLoadingStatus({
                key: columnId,
                loading: {
                  isInitialLoading: true,
                  isLoadingMore: false,
                },
              })
            );

            dispatch(
              setPagination({
                key: columnId,
                pagination: {
                  page: 1,
                  limit: PAGE_LIMIT,
                },
              })
            );

            dispatch(
              setHasMore({
                key: columnId,
                hasMore: true,
              })
            );

            previousTasksRef.current[columnId] = 0;

            const updatedGroupData = groupingData[columnId];

            if (updatedGroupData) {
              await fetchBugsForGroup(
                columnId,
                updatedGroupData,
                1,
                PAGE_LIMIT,
                false
              );
            }
          }
          if (updatedColumnDataRef.current[columnId]) {
            dispatch(
              updateColumn({
                columnId,
                column: updatedColumnDataRef.current[columnId],
              })
            );

            const restoredGroupingData = {
              ...groupingData,
              [columnId]: {
                ...groupingData[columnId],
                ...updatedColumnDataRef.current[columnId],
              },
            };
            dispatch(setGroupingData(restoredGroupingData));

            // Clean up
            delete updatedColumnDataRef.current[columnId];
          }
        }
      } catch (err: any) {
        dispatch(
          updateColumn({
            columnId,
            column: {
              title: oldTitle,
            },
          })
        );
        dispatch(
          setGroupingData({
            ...groupingData,
            [columnId]: {
              ...groupingData[columnId],
              title: oldTitle,
              icon: oldIcon,
            },
          })
        );
        toast.error(err?.data?.message || "Failed to update column title");
      }
    },
    [
      groupBy,
      isMyTaskView,
      columns,
      updateStatus,
      id,
      viewType,
      groupingData,
      PAGE_LIMIT,
      previousTasksRef,
      groupingData,
      fetchBugsForGroup,
    ]
  );

  const handleColumnDeleteRequest = useCallback(
    (columnId: string) => {
      if (groupBy !== "Status") {
        toast.error("Can only delete columns when grouping by Status");
        return;
      }

      if (isMyTaskView) {
        toast.error("Cannot delete columns in My Tasks view");
        return;
      }

      const column = columns[columnId];
      if (!column) return;

      dispatch(
        setDeleteConfirmModal({
          open: true,
          column: {
            id: columnId,
            title: column.title,
          },
        })
      );
    },
    [groupBy, isMyTaskView, columns, dispatch]
  );

  const handleColumnDelete = useCallback(async () => {
    if (!columnToDelete || groupBy !== "Status" || isMyTaskView) return;

    const { id: columnId } = columnToDelete;

    try {
      await deleteStatus(columnId).unwrap();

      toast.success("Column deleted successfully");

      if (viewType === "kanban") {
        const newColumns = { ...columns };
        delete newColumns[columnId];
        dispatch(setColumns(newColumns));

        dispatch(setColumnOrder(columnOrder.filter((id) => id !== columnId)));

        // Clean up related state
        if (previousTasksRef.current[columnId]) {
          delete previousTasksRef.current[columnId];
        }

        dispatch(
          setScrollPosition({
            columnId,
            position: 0,
          })
        );
      }

      dispatch(
        setDeleteConfirmModal({
          open: false,
          column: null,
        })
      );
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete column");
    }
  }, [
    columnToDelete,
    groupBy,
    isMyTaskView,
    deleteStatus,
    viewType,
    columns,
    columnOrder,
    previousTasksRef,
  ]);

  const handleDeleteConfirmClose = useCallback(() => {
    dispatch(
      setDeleteConfirmModal({
        open: false,
        column: null,
      })
    );
  }, [dispatch]);

  return {
    addColumn,
    handleColumnTitleChange,
    handleColumnDeleteRequest,
    handleColumnDelete,
    handleDeleteConfirmClose,
    isCreatingStatus,
    isDeletingStatus,
  };
};
